/**
 * The node and mesh names Godot 4.6.3's glTF importer gives a file: `_assign_node_names`
 * (`gltf_document.cpp:3968-3996`), the bone names of `_create_skeletons` (`skin_tool.cpp:560-628`)
 * and the parse steps they depend on. A scene's node path into an instanced glTF names these, not
 * the names three's loader gives.
 */
import { gltfObjects, isGltfJsonObject, usesGodotSingleRoot } from './gltf.js';
import { gltfImportScene } from './gltfImportScene.js';
import { gltfSkeletonBones } from './gltfSkeletons.js';
import { validateNodeName } from '../string/nodeName.js';
/** `gltf/naming_version`'s default (`editor_scene_importer_gltf.cpp:87`). */
export const DEFAULT_GLTF_NAMING_VERSION = 2;
/** Godot's `unique_names`, filled by `_gen_unique_name_static` (`gltf_document.cpp:7223-7242`). */
class UniqueNames {
    /** Grows only: `take` adds node and mesh names, and `boneNames` adds bone names before 4.5. */
    used = new Set();
    /** Each base's first number not yet tried. `used` only grows, so every number below it is taken. */
    nextIndex = new Map();
    /** The validated name, numbered from 2 until it is unused. */
    take(name) {
        const base = validateNodeName(name);
        let unique = base;
        let index = this.nextIndex.get(base) ?? 2;
        if (this.used.has(unique)) {
            for (unique = `${base}${index}`; this.used.has(unique); unique = `${base}${index}`)
                index++;
            this.nextIndex.set(base, index + 1);
        }
        this.used.add(unique);
        return unique;
    }
}
/** `_gen_unique_bone_name` (`skin_tool.cpp:792-812`): `_2` onwards, unlike a node name. */
function uniqueBoneName(used, name) {
    const base = name.replace(/[:/]/g, '_') || 'bone';
    let unique = base;
    for (let index = 2; used.has(unique); index++)
        unique = `${base}_${index}`;
    used.add(unique);
    return unique;
}
/**
 * The bone names by node. Before 4.5 every bone is unique against the node names and every other
 * bone. From 4.5 a bone is unique against the node names and its own skeleton's bones.
 */
function boneNames(nodes, skeletons, usedNames, namingVersion) {
    const byNode = new Map();
    for (const bones of skeletons) {
        const used = namingVersion < 2 ? usedNames : new Set(usedNames);
        for (const bone of bones)
            byNode.set(bone, uniqueBoneName(used, text(nodes[bone]['name'])));
    }
    return byNode;
}
function indexes(value) {
    return Array.isArray(value) ? value.filter((entry) => Number.isInteger(entry)) : [];
}
function index(value) {
    return Number.isInteger(value) ? value : -1;
}
function text(value) {
    return typeof value === 'string' ? value : '';
}
/** `_parse_nodes` (`gltf_document.cpp:563-652`): the graph with parents and heights. */
function importNodes(nodes) {
    const parents = nodes.map(() => -1);
    nodes.forEach((node, i) => {
        for (const child of indexes(node['children'])) {
            // `ERR_CONTINUE` keeps a child's first parent.
            if (child < nodes.length && parents[child] === -1)
                parents[child] = i;
        }
    });
    const heights = new Map();
    const heightOf = (node) => {
        const parent = parents[node];
        if (parent < 0)
            return 0;
        const height = heights.get(node) ?? 1 + heightOf(parent);
        heights.set(node, height);
        return height;
    };
    return nodes.map((node, i) => ({
        children: indexes(node['children']).filter((child) => child < nodes.length),
        parent: parents[i],
        height: heightOf(i),
        mesh: index(node['mesh']),
        skin: index(node['skin']),
        joint: false,
    }));
}
/** `_parse_scenes` (`gltf_document.cpp:527-561`): the loaded scene's root nodes and its name. */
function loadedScene(json, fileName) {
    const scenes = gltfObjects(json['scenes']);
    const scene = scenes[Number.isInteger(json['scene']) ? json['scene'] : 0];
    if (!scene)
        return { roots: [], name: '' };
    const name = text(scene['name']);
    return { roots: indexes(scene['nodes']), name: name !== '' && !name.startsWith('Scene') ? name : fileName };
}
/** The name `_assign_node_names` gives an unnamed node, by what it holds. */
function unnamedNodeName(node, namingVersion) {
    if (index(node['mesh']) >= 0)
        return 'Mesh';
    if (index(node['camera']) >= 0)
        return namingVersion === 0 ? 'Camera3D' : 'Camera';
    return 'Node';
}
/** The names of `json`, the parsed glTF document. A document with no `nodes` names none. */
export function gltfImportNames(json, options) {
    if (!isGltfJsonObject(json))
        return { nodes: [], meshes: [], animations: [], scene: [] };
    const { namingVersion, importAsSkeletonBones, fileName } = options;
    const names = new UniqueNames();
    // `_parse_scenes` reserves the name before it reads anything (`gltf_document.cpp:528`).
    names.take('Skeleton3D');
    const loaded = loadedScene(json, fileName);
    const sceneName = namingVersion === 0 && gltfObjects(json['scenes']).length > 0 ? names.take(loaded.name) : loaded.name;
    const nodeObjects = gltfObjects(json['nodes']);
    const nodes = importNodes(nodeObjects);
    // Before 4.5 the roots are every parentless node (`gltf_document.cpp:655-676`).
    const roots = namingVersion < 2 ? nodes.flatMap((node, i) => (node.parent < 0 ? [i] : [])) : loaded.roots;
    const skinJoints = gltfObjects(json['skins']).map((skin) => indexes(skin['joints']).filter((j) => j < nodes.length));
    const skeletons = gltfSkeletonBones(nodes, skinJoints, importAsSkeletonBones ? roots : [], importAsSkeletonBones || namingVersion < 2);
    const skeletonOfBone = new Map(skeletons.flatMap((bones, i) => bones.map((bone) => [bone, i])));
    const bones = new Set(skeletonOfBone.keys());
    const sceneNames = nodeObjects.map((node, i) => {
        if (bones.has(i))
            return null;
        const name = text(node['name']);
        if (name !== '')
            return names.take(name);
        const unnamed = unnamedNodeName(node, namingVersion);
        return names.take(namingVersion === 0 ? names.take(unnamed) : unnamed);
    });
    // `_parse_meshes` names each mesh before `_create_skeletons` names a bone (`gltf_document.cpp:1434`).
    const meshes = gltfObjects(json['meshes']).map((mesh) => names.take(`${sceneName}_${text(mesh['name']) || 'mesh'}`));
    const bonesByNode = boneNames(nodeObjects, skeletons, names.used, namingVersion);
    const nodeNames = sceneNames.map((name, i) => name === null ? { name: validateNodeName(bonesByNode.get(i)), isBone: true } : { name, isBone: false });
    const animations = gltfObjects(json['animations']);
    const scene = gltfImportScene({
        nodes: nodes.map((node, i) => sceneSourceNode(node, nodeObjects[i], nodeNames[i].name)),
        rootNodes: roots.filter((root) => root < nodes.length),
        skeletons,
        skinSkeletons: skinJoints.map((joints) => skeletonOfBone.get(joints[0] ?? -1) ?? -1),
        singleRoot: usesGodotSingleRoot(json),
        namingVersion,
        hasAnimations: animations.some(isImportedAnimation),
    });
    return { nodes: nodeNames, meshes, animations: animationNames(animations), scene };
}
/** `_parse_nodes` (`gltf_document.cpp:563-652`): the light a `KHR_lights_punctual` node holds, or -1. */
function lightIndex(node) {
    const extensions = node['extensions'];
    const light = isGltfJsonObject(extensions) ? extensions['KHR_lights_punctual'] : undefined;
    return isGltfJsonObject(light) ? index(light['light']) : -1;
}
function sceneSourceNode(node, object, name) {
    const { children, parent, joint, mesh, skin } = node;
    return {
        name,
        children,
        parent,
        joint,
        mesh,
        skin,
        camera: index(object['camera']),
        light: lightIndex(object),
    };
}
/** Whether `_parse_animations` keeps the animation: it needs channels and samplers (`:3741-3743`). */
function isImportedAnimation(animation) {
    return Array.isArray(animation['channels']) && Array.isArray(animation['samplers']);
}
/** `_parse_animations` (`gltf_document.cpp:3735-3756`): each named animation that has channels and samplers. */
function animationNames(animations) {
    const taken = new Set();
    return animations.map((animation) => {
        if (!isImportedAnimation(animation) || typeof animation['name'] !== 'string')
            return null;
        const name = animation['name'];
        const lower = name.toLowerCase();
        const loops = ['loop', 'cycle'].some((word) => lower.startsWith(word) || lower.endsWith(word));
        return { name: uniqueAnimationName(name, taken), loops };
    });
}
/**
 * `_gen_unique_animation_name` (`gltf_document.cpp:475-495`): the name as `_sanitize_animation_name`
 * leaves it (`:470-473`), or the first of `name2`, `name3` and so on that no earlier animation took.
 */
function uniqueAnimationName(name, taken) {
    const sanitised = validateNodeName(name).replace(/[/:,[]/g, '_');
    let unique = sanitised;
    for (let index = 2; taken.has(unique); index++)
        unique = `${sanitised}${index}`;
    taken.add(unique);
    return unique;
}
//# sourceMappingURL=gltfImportNames.js.map